import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaCheck, FaEdit, FaTrash, FaPlus, FaPen } from "react-icons/fa";
import { useSelector } from "react-redux";
import DropdownPortal from "./common/DropdownPortal";
import { FaX } from "react-icons/fa6";
import { arClass } from "../utils/useArClass";

// Extracted static texts for different languages
const selectTexts = {
  en: {
    searchPlaceholder: "Search...",
    createPlaceholder: "Add custom option",
    createLabelPlaceholder: "New option label",
    noResults: "No results",
    customLabel: "(custom)",
    selectOptions: "Select options",
    selectOption: "Select an option",
    addButton: "Add",
    cancelButton: "Cancel",
    editButton: "Edit",
    deleteButton: "Delete",
    saveButton: "Save",
  },
  fr: {
    searchPlaceholder: "Rechercher...",
    createPlaceholder: "Ajouter une option personnalisée",
    createLabelPlaceholder: "Nouveau libellé d'option",
    noResults: "Aucun résultat",
    customLabel: "(personnalisé)",
    selectOptions: "Sélectionner des options",
    selectOption: "Sélectionner une option",
    addButton: "Ajouter",
    cancelButton: "Annuler",
    editButton: "Modifier",
    deleteButton: "Supprimer",
    saveButton: "Enregistrer",
  },
  ar: {
    searchPlaceholder: "بحث...",
    createPlaceholder: "إضافة خيار مخصص",
    createLabelPlaceholder: "تسمية الخيار الجديد",
    noResults: "لا توجد نتائج",
    customLabel: "(مخصص)",
    selectOptions: "اختر الخيارات",
    selectOption: "اختر خيارًا",
    addButton: "إضافة",
    cancelButton: "إلغاء",
    editButton: "تعديل",
    deleteButton: "حذف",
    saveButton: "حفظ",
  },
};

const CustomSelect = ({
  value,
  onChange,
  options = [],
  className = "",
  name,
  readOnly = false,
  disabled = false,
  mainClassName = "",
  allowCreate = true,
  allowSearch = true,
  createPlaceholder = null,
  allowCentered = false,
  onCreate,
  multipleSelection = false,
}) => {
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage) || "en";
  const darkMode = useSelector((state) => state.settings.darkMode);
  const sidebarStyle = useSelector((state) => state.settings.sidebarStyle);
  
  // Get texts based on language
  const texts = selectTexts[defaultLanguage] || selectTexts.en;
  const isRTL = defaultLanguage === "ar";
  
  // Use custom placeholder or fallback to language-specific text
  const actualCreatePlaceholder = createPlaceholder || texts.createPlaceholder;
  
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [createLabel, setCreateLabel] = useState("");
  const [localOptions, setLocalOptions] = useState(() =>
    (options || []).map((o) => ({ ...o, _created: false }))
  );
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    const incoming = (options || []).map((o) => ({ ...o, _created: false }));
    const created = localOptions.filter((o) => o._created);
    setLocalOptions([...created, ...incoming]);
  }, [options]);

  const getColorScheme = () => {
    const entry = {
      light: {
        bg: "bg-white text-gray-900",
        color: "!text-gray-900",
        subBg: "!bg-gray-50/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-200",
        active: "!bg-gray-200 !text-gray-900 !border-gray-400 !shadow",
        iconBg: "!bg-gray-300",
        glow: "!shadow-lg !shadow-gray-300/20",
      },
      dark: {
        bg: "bg-gray-600 !text-gray-50",
        color: "!text-gray-50",
        subBg: "!bg-gray-700/90 !backdrop-blur-sm",
        border: "!border-gray-300",
        hover: "hover:!bg-gray-800",
        active: "!bg-gray-600 !text-white !border-gray-500 shadow",
        iconBg: "!bg-gray-600",
        glow: "!shadow-lg !shadow-gray-700/30",
      },
    };

    return darkMode ? entry.dark : entry.light;
  };

  const colors = getColorScheme();

  const applySidebarStyle = (colors) => {

      return {
        containerActive: `${colors.active} ${colors.border} ${colors.glow} border`,
        containerInactive: `${colors.bg} ${colors.border} ${colors.hover} border hover:shadow-lg`,
        subBg: colors.subBg,
        optionHover: `${colors.hover} ${colors.border}`,
        optionActive: `${colors.active} ${colors.border}`,
      };
   
  };

  const styled = applySidebarStyle(colors);

  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(null);

  useEffect(() => {
    const updateWidth = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownWidth(Math.round(rect.width));
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [options]);

  // Focus search input when dropdown opens but prevent the browser from scrolling
  useEffect(() => {
    if (!allowSearch) return;
    if (isOpen && !showCreateInput) {
      setTimeout(() => {
        if (searchInputRef.current && typeof searchInputRef.current.focus === "function") {
          try {
            searchInputRef.current.focus({ preventScroll: true });
          } catch (e) {
            // Fallback for browsers that don't support preventScroll
            const x = window.scrollX,
              y = window.scrollY;
            searchInputRef.current.focus();
            window.scrollTo(x, y);
          }
        }
      }, 0);
    }
  }, [isOpen, allowSearch, showCreateInput]);

  // Focus create input when the create input is shown (also prevent scrolling)
  useEffect(() => {
    if (!showCreateInput) return;
    setTimeout(() => {
      const el = document.getElementById("custom-select-create-input");
      if (el && typeof el.focus === "function") {
        try {
          el.focus({ preventScroll: true });
        } catch (e) {
          const x = window.scrollX,
            y = window.scrollY;
          el.focus();
          window.scrollTo(x, y);
        }
      }
    }, 0);
  }, [showCreateInput]);

  const filteredOptions = useMemo(() => {
    const q = (query || "").toString().trim().toLowerCase();
    const created = localOptions.filter((o) => o._created);
    const incoming = (localOptions.filter((o) => !o._created) || []).filter((opt) =>
      q ? (opt.label || "").toString().toLowerCase().includes(q) : true
    );
    return [...created, ...incoming];
  }, [localOptions, query]);

  const slugify = (str = "") =>
    str
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");

  const equals = (a, b) =>
    a === b || (a != null && b != null && String(a) === String(b));

  const dropdownVariants = {
    hidden: { opacity: 0, scale: 0.95, y: -10 },
    visible: { opacity: 1, scale: 1, y: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: isRTL ? 4 : -4 },
    visible: { opacity: 1, x: 0 },
  };

  const handleCreateAdd = () => {
    const label = (createLabel || "").trim();
    if (!label) return;
    const val = label;
    const newOption = { label, value: val, _created: true };
    setLocalOptions((prev) => [newOption, ...prev.filter((o) => o.value !== val)]);
    onCreate && onCreate(newOption);

    if (typeof onChange === "function") {
      if (multipleSelection) {
        const current = Array.isArray(value) ? value : [];
        onChange({ target: { name, value: [...current, val] } });
      } else {
        onChange({ target: { name, value: val } });
      }
    }

    setCreateLabel("");
    setShowCreateInput(false);
    setIsOpen(false);
    setQuery("");
  };

  const handleSelectOption = (option) => {
    if (multipleSelection) {
      const current = Array.isArray(value) ? value : [];
      const alreadySelected = current.some((v) => equals(v, option.value));
      const newValues = alreadySelected
        ? current.filter((v) => !equals(v, option.value))
        : [...current, option.value];
      if (typeof onChange === "function") onChange({ target: { name, value: newValues } });
      return;
    } else {
      const already = equals(value, option.value) || equals(value, option.label);
      const newVal = already ? null : option.value;
      if (typeof onChange === "function") onChange({ target: { name, value: newVal } });
      setIsOpen(false);
      setQuery("");
    }
  };

  const handleDeleteCreated = (opt) => {
    setLocalOptions((prev) => prev.filter((o) => o.value !== opt.value));
    if (multipleSelection && Array.isArray(value) && value.includes(opt.value)) {
      onChange({ target: { name, value: value.filter((v) => v !== opt.value) } });
    } else if (value === opt.value) {
      onChange({ target: { name, value: null } });
    }
  };

  const startEdit = (opt) => {
    setEditingId(opt.value);
    setEditLabel(opt.label);
    setEditValue(opt.value);
  };

  const saveEdit = (origValue) => {
    const label = (editLabel || "").trim();
    const val = (editValue || "").trim() || `${slugify(label)}-${Date.now()}`;
    if (!label) return;
    const origOpt = localOptions.find((o) => o.value === origValue) || {};
    const origLabel = origOpt.label || origValue;

    setLocalOptions((prev) =>
      prev.map((o) =>
        o.value === origValue ? { ...o, label, value: val, _created: true } : o
      )
    );

    if (multipleSelection && Array.isArray(value)) {
      const newVals = value.map((v) => (v === origValue ? val : v));
      onChange({ target: { name, value: newVals } });
    } else if (value === origValue || value === origLabel) {
      onChange({ target: { name, value: val } });
    }

    onCreate && onCreate({ label, value: val, updatedFrom: origValue });
    setEditingId(null);
    setEditLabel("");
    setEditValue("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
    setEditValue("");
  };

  const selectedOptions = useMemo(() => {
    if (multipleSelection) {
      const current = Array.isArray(value) ? value : [];
      return localOptions.filter(
        (opt) => current.includes(opt.value)
      );
    } else {
      const findMatch = (opt) =>
        opt.value === value ||
        opt.label === value ||
        String(opt.value) === String(value);

      return (
        localOptions.find(findMatch) ||
        options.find(findMatch) ||
        null
      );
    }
  }, [value, localOptions, options, multipleSelection]);

  const displayText = useMemo(() => {
    if (multipleSelection) {
      return selectedOptions && selectedOptions.length > 0
        ? selectedOptions.map((o) => o.label).join(", ")
        : texts.selectOptions;
    } else {
      if (selectedOptions && selectedOptions.label) return selectedOptions.label;
      if (value !== undefined && value !== null && String(value) !== "") return String(value);
      return texts.selectOption;
    }
  }, [selectedOptions, multipleSelection, value, texts]);

  // RTL styling classes
  const rtlClasses = isRTL ? "rtl text-right justify-end " : "ltr text-left justify-start ";
  const flexDirectionClass = isRTL ? "flex-row-reverse" : "";
  const textAlignClass = isRTL ? "text-right" : "text-left";
  const marginDirectionClass = isRTL ? "ml-0 mr-2" : "ml-2 mr-0";

  return (
    <div className={arClass(`relative w-full z-[100] flex  ${mainClassName} ${rtlClasses}`)}>
      <motion.button
        type="button"
        ref={buttonRef}
        className={arClass(`w-full hover:!transform-none flex items-center justify-between gap-3 p-3 rounded-md text-sm font-medium transition-all duration-300 max-sm:text-xs max-sm:p-2 ${
          isOpen ? styled.containerActive : styled.containerInactive
        } ${className}`)}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
          if (!isOpen) setQuery("");
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className={arClass(`max-sm:text-xs ${colors.color} ${textAlignClass}`)}>{displayText}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 20 }}
          className={arClass(`max-sm:text-xs ${colors.color}`)}
        >
          <FaChevronDown className={`text-xs`} />
        </motion.span>
      </motion.button>

      <AnimatePresence>
        {isOpen && !readOnly && (
          <DropdownPortal anchorRef={buttonRef} onClose={() => setIsOpen(false)} className={`${styled.subBg} border ${colors.border} shadow-lg max-sm:text-xs ${
            allowCentered ? 'max-sm:!top-[calc(100%-70%)]' : ''
          } ${textAlignClass}`}>
            <motion.div
              className={`mt-2 rounded-md overflow-hidden z-[100]`}
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {
                allowSearch && (
                  <div className="p-2 border-b border-transparent normal-form">
                <input
                  type="text"
                  ref={searchInputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={texts.searchPlaceholder}
                  className={`w-full p-3 rounded-md text-sm !border max-sm:text-xs max-sm:p-2 ${textAlignClass} ${
                    darkMode
                      ? "!bg-gray-800 !text-gray-100 !placeholder-gray-400"
                      : "!bg-white !text-gray-800 !placeholder-gray-500 !border-gray-400"
                  }`}
                />
              </div>
                )
              }

              {allowCreate && (
                <div className="p-2 border-b border-transparent max-sm:text-xs">
                  {!showCreateInput ? (
                    <button
                      type="button"
                      className={`w-full ${textAlignClass} px-3 py-3 rounded-md text-sm font-medium flex items-center gap-2 max-sm:text-xs max-sm:p-2 ${styled.optionHover} border ${styled.containerActive} ${flexDirectionClass}`}
                      onClick={() => setShowCreateInput(true)}
                    >
                      <FaPlus size={12} className={`max-sm:text-xs ${colors.color}`} />
                      <span className={`max-sm:text-xs ${colors.color}`}>{actualCreatePlaceholder}</span>
                    </button>
                  ) : (
                    <div className={`flex gap-2 items-center normal-form ${flexDirectionClass}`}>
                      <input
                        id="custom-select-create-input"
                        type="text"
                        value={createLabel}
                        onChange={(e) => setCreateLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCreateAdd();
                          }
                          if (e.key === "Escape") {
                            setShowCreateInput(false);
                            setCreateLabel("");
                          }
                        }}
                        placeholder={texts.createLabelPlaceholder}
                        className={`flex-1 px-3 py-3 rounded-md text-sm max-sm:text-xs max-sm:p-2 ${textAlignClass} ${
                          darkMode
                            ? "bg-gray-800 text-gray-100"
                            : "bg-white text-gray-800"
                        } border ${
                          darkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      />
                     
                      <button
                        type="button"
                        onClick={handleCreateAdd}
                        className={`p-3 rounded-md max-sm:text-xs max-sm:p-2 ${darkMode ? "bg-green-700 text-green-200" : "bg-green-200 text-green-800"}`}
                        title={texts.addButton}
                      >
                        <FaPlus className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateInput(false);
                          setCreateLabel("");
                        }}
                        className={`p-3 rounded-md max-sm:text-xs max-sm:p-2 ${darkMode ? "bg-red-700 text-red-200" : "bg-red-200 text-red-800"}`}
                        title={texts.cancelButton}
                      >
                        <FaX className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="max-h-64 overflow-y-auto p-1 flex flex-col gap-1">
                {filteredOptions.length === 0 ? (
                  <div className={`p-3 text-sm text-gray-500 ${textAlignClass}`}>{texts.noResults}</div>
                ) : (
                  filteredOptions.map((option) => {
                    const isCreated = option._created === true;
                    const isEditing = editingId === option.value;
                    const createdStyle = isCreated
                      ? darkMode
                        ? "bg-green-700/20 border border-green-600"
                        : "bg-green-50 border border-green-200"
                      : "";
                    const isActiveStyle = isCreated
                      ? darkMode ? "bg-green-900/30 hover:bg-green-green-800" : "bg-green-100 hover:bg-green-green-200"
                      : styled.optionActive;
                    const unActiveStyle = isCreated
                      ? darkMode ? "hover:!bg-green-500/30" : "hover:!bg-green-300/30"
                      : `${`max-sm:text-xs ${colors.color}`} ${styled.optionHover}`;
                    const isActive = multipleSelection
                      ? Array.isArray(value) &&
                        value.some((v) => equals(v, option.value))
                      : value != null && (equals(option.value, value) || equals(option.label, value));
                   
                    return (
                      <motion.div
                        key={option.value}
                        className={`w-full hover:!transform-none rounded-md flex items-center text-sm font-medium transition-all duration-200 ${
                          isActive 
                            ? isActiveStyle
                            : unActiveStyle
                        } ${createdStyle}
                        ${isEditing ? "!bg-transparent hover:!bg-transparent hover:!border-none !border-none gap-2" : "px-2"}
                        ${flexDirectionClass}
                        `}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {!isEditing ? (
                          <>
                            <button
                              type="button"
                              className={`${textAlignClass} p-3 max-sm:p-2 flex-1`}
                              onClick={() => handleSelectOption(option)}
                              disabled={disabled}
                            >
                              <span className={`max-sm:text-xs ${colors.color}`}>{option.label}</span>
                              {isCreated && (
                                <span className={`${marginDirectionClass} text-xs text-gray-500 max-sm:text-xs`}>
                                  {texts.customLabel}
                                </span>
                              )}
                            </button>
                            {isCreated && (
                              <div className={`flex items-center gap-2 ${isRTL ? 'mr-auto' : 'ml-auto'} ${flexDirectionClass}`}>
                                <button 
                                  type="button"
                                  onClick={() => startEdit(option)}
                                  className={`p-1 rounded shadow-sm max-sm:text-xs max-sm:p-2 ${darkMode ? "bg-green-700 text-green-200" : "bg-green-200 text-green-800"}`}
                                  title={texts.editButton}
                                >
                                  <FaPen className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteCreated(option)}
                                  className={`p-1 rounded shadow-sm max-sm:text-xs max-sm:p-2 ${darkMode ? "bg-red-700 text-red-200" : "bg-red-200 text-red-800"}`}
                                  title={texts.deleteButton}
                                >
                                  <FaTrash className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                                </button>
                                {isActive && (
                                  <span
                                    className={`p-1 rounded shadow-sm ${darkMode ? "bg-green-400 text-green-900" : "bg-green-200 text-green-800"}`}>
                                    <FaCheck className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className={`flex-1 flex items-center gap-2 w-full normal-form ${flexDirectionClass}`}>
                            <input
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              className={`flex-1 px-3 py-3 rounded-md text-sm max-sm:text-xs max-sm:p-2 ${textAlignClass} ${
                                darkMode
                                  ? "bg-gray-800 text-gray-100"
                                  : "bg-white text-gray-800"
                              } border ${
                                darkMode ? "border-green-700" : "border-green-200"
                              }`}
                            />
                            <div className={`flex items-center gap-2 ${isRTL ? 'mr-auto' : 'ml-auto'} ${flexDirectionClass}`}>
                              <button
                                type="button"
                                onClick={() => saveEdit(option.value)}
                                className={`p-3 rounded shadow-sm max-sm:p-2 ${darkMode ? "bg-green-700 text-green-200" : "bg-green-200 text-green-800"}`}
                                title={texts.saveButton}
                              >
                                <FaCheck className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                className={`p-3 rounded shadow-sm max-sm:p-2 ${darkMode ? "bg-red-700 text-red-200" : "bg-red-200 text-red-800"}`}
                                title={texts.cancelButton}
                              >
                                <FaX className="w-4 h-4 max-sm:w-3 max-sm:h-3 " />
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </DropdownPortal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomSelect;